import clsx from 'clsx'
import { Badge, Button, Col, Grid, Icon, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import formatMoney from 'utils/formatMoney'
import downloadFile from 'utils/downloadFile'
import { APP_PRODUCT } from 'constants/app-config'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import styles from 'modules/Finance/Invoices/Invoices.module.scss'

const STATUSES_SETTING = {
  paid: {
    icon: 'check',
  },
  canceled: {
    icon: 'disabled',
  },
  waiting: {
    icon: 'time-clock',
  },
  new: {
    icon: 'star',
  },
  pending: {
    icon: 'rotate',
  },
}

const Item = ({
  id = null,
  amount = '',
  currency = '',
  className = '',
  status,
  invoice_number,
  payment_profile: { title = null } = {},
}) => {
  const { t } = useTranslation()
  const breakpoint = useBreakpoint()

  return (
    <Grid
      justifyContent={'between'}
      wrap={false}
      className={clsx(className, styles['list__item'])}
      gap={8}
    >
      <Col className={styles['list__item-col']}>
        <Grid
          direction={'row'}
          gap={16}
          wrap={false}
          alignItems={'center'}
        >
          <Grid
            alignItems={'center'}
            fluid={false}
            justifyContent={'center'}
            className={clsx(styles['list__item-badge'], styles['list__item-badge_' + status])}
          >
            <Icon
              width={20}
              name={STATUSES_SETTING?.[status]?.icon ?? 'disabled'}
            />
          </Grid>

          <Grid
            direction={'column'}
            gap={4}
            alignItems={'start'}
            justifyContent={'start'}
            className={styles['list__item-info']}
          >
            <Typography
              variant={'Body-15'}
              color={'Label-Fifth'}
              className={styles['list__item-invoice-number']}
              noWrap
              component={'div'}
            >
              {invoice_number}
            </Typography>
            <Grid
              direction={breakpoint.sm ? 'row' : 'column'}
              gap={8}
              alignItems={breakpoint.sm ? 'center' : 'start'}
              wrap={false}
            >
              {status && (
                <Badge
                  rounded
                  size={'xs'}
                  bgColor={'Background-Fifth'}
                  variant={'Body-10'}
                  color={'Label-Fifth'}
                >
                  {t(`invoices::statuses.${status}`)}
                </Badge>
              )}
              {title && (
                <Typography
                  variant={'Body-10'}
                  color={'Label-Secondary'}
                >
                  {title}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Col>
      <Col className={styles['list__item-col']}>
        <Grid
          gap={8}
          alignItems={'center'}
          justifyContent={'end'}
          direction={breakpoint.sm ? 'row' : 'column-reverse'}
          wrap={false}
        >
          {!['new', 'canceled'].includes(status) && (
            <Grid
              fluid={!breakpoint.sm}
              justifyContent={breakpoint.sm ? 'start' : 'end'}
            >
              <Button
                variant={'ghost'}
                noLabel
                grey
                size={'s'}
                startIcon={<Icon name={'download'} />}
                onClick={() =>
                  downloadFile(
                    `/api/app/v1/billing/invoices/${id}/document`,
                    `${APP_PRODUCT}-'invoice-'${invoice_number}`,
                  )
                }
              />
            </Grid>
          )}
          <Grid
            className={styles['list__item-amount']}
            justifyContent={'end'}
          >
            <Typography
              variant={'Body-20'}
              color={'Label-Fifth'}
              component={'div'}
              noWrap
              dangerouslySetInnerHTML={{ __html: formatMoney(amount, currency) }}
            />
          </Grid>
        </Grid>
      </Col>
    </Grid>
  )
}

export default Item

Item.propTypes = {
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currency: PropTypes.string,
  action: PropTypes.string,
  heading: PropTypes.string,
  direction: PropTypes.string,
  status: PropTypes.string,
  id: PropTypes.number,
  created_at: PropTypes.string,
  className: PropTypes.string,
  document: PropTypes.object,
  invoice_number: PropTypes.string,
  payment_profile: PropTypes.object,
}
